/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListServerClientAssociationsRequest,
  ListServerClientAssociationsSecurity,
} from "../models/operations/listserverclientassociations.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildServerClientAssociationsQuery,
  prefetchServerClientAssociations,
  queryKeyServerClientAssociations,
  ServerClientAssociationsQueryData,
} from "./serverClientAssociations.core.js";
export {
  buildServerClientAssociationsQuery,
  prefetchServerClientAssociations,
  queryKeyServerClientAssociations,
  type ServerClientAssociationsQueryData,
};

export type ServerClientAssociationsQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listServerClientAssociations remoteSessions
 *
 * @remarks
 * List the upstream OAuth clients an MCP server is associated with. An association is a per-server configuration fact: one client can serve several servers, and server_count reports how many live servers share each client, since editing or rotating a shared client changes every one of them. Associations are recorded ahead of the serving path that will read them; until then the server keeps signing in through its user session issuer's bindings. Requires mcp:read on the server.
 */
export function useServerClientAssociations(
  request: ListServerClientAssociationsRequest,
  security?: ListServerClientAssociationsSecurity | undefined,
  options?: QueryHookOptions<
    ServerClientAssociationsQueryData,
    ServerClientAssociationsQueryError
  >,
): UseQueryResult<
  ServerClientAssociationsQueryData,
  ServerClientAssociationsQueryError
> {
  const client = useGramContext();
  return useQuery({
    ...buildServerClientAssociationsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listServerClientAssociations remoteSessions
 *
 * @remarks
 * List the upstream OAuth clients an MCP server is associated with. An association is a per-server configuration fact: one client can serve several servers, and server_count reports how many live servers share each client, since editing or rotating a shared client changes every one of them. Associations are recorded ahead of the serving path that will read them; until then the server keeps signing in through its user session issuer's bindings. Requires mcp:read on the server.
 */
export function useServerClientAssociationsSuspense(
  request: ListServerClientAssociationsRequest,
  security?: ListServerClientAssociationsSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    ServerClientAssociationsQueryData,
    ServerClientAssociationsQueryError
  >,
): UseSuspenseQueryResult<
  ServerClientAssociationsQueryData,
  ServerClientAssociationsQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildServerClientAssociationsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setServerClientAssociationsData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      mcpServerId: string;
      gramSession?: string | undefined;
      gramKey?: string | undefined;
      gramProject?: string | undefined;
    },
  ],
  data: ServerClientAssociationsQueryData,
): ServerClientAssociationsQueryData | undefined {
  const key = queryKeyServerClientAssociations(...queryKeyBase);

  return client.setQueryData<ServerClientAssociationsQueryData>(key, data);
}

export function invalidateServerClientAssociations(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      mcpServerId: string;
      gramSession?: string | undefined;
      gramKey?: string | undefined;
      gramProject?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: [
      "@gram/client",
      "remoteSessions",
      "listServerClientAssociations",
      ...queryKeyBase,
    ],
  });
}

export function invalidateAllServerClientAssociations(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: [
      "@gram/client",
      "remoteSessions",
      "listServerClientAssociations",
    ],
  });
}
